import { ModalState } from '@frontend/core'
import { toggleModal } from '@frontend/core/lib/modules/modal/slice'
import { cn } from '@frontend/ui'
import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { Provider, ReactReduxContext } from 'react-redux'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { prefersReducedMotion } from '../../utils'
import styles from './modal.module.scss'

export const Modal: React.FC = () => {
  const modal = useAppSelector((state) => state.modal)
  const dispatch = useAppDispatch()

  const handleToggleModal = (payload: Partial<ModalState>) => {
    dispatch(toggleModal(payload))
  }

  return (
    <div>
      <ModalOverlay active={modal.active} toggleModal={handleToggleModal} />
      <Portal>
        {modal.active && (
          <div className={styles.modalContainer}>
            <div
              style={{
                width: modal.width ? modal.width : '35%',
                height: modal.height ? modal.height : 'auto'
              }}
              className={`${
                styles[modal.className || 'modalSmall']
              } bg-background`}
            >
              <ModalContent modal={modal} />
            </div>
          </div>
        )}
      </Portal>
    </div>
  )
}

interface PortalProps {
  children: React.ReactNode
}

export const Portal: React.FC<PortalProps> = ({ children }) => {
  const portalRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    // Create portal element on mount
    const portal = document.createElement('div')
    portal.className = styles.portal
    document.body.prepend(portal)
    portalRef.current = portal

    // Cleanup on unmount
    return () => {
      if (portalRef.current && document.body.contains(portalRef.current)) {
        document.body.removeChild(portalRef.current)
      }
    }
  }, [])

  // Don't render until portal is created
  if (!portalRef.current) {
    return null
  }

  return ReactDOM.createPortal(
    <ReactReduxContext.Consumer>
      {(ctx) =>
        ctx ? <Provider store={ctx.store}>{children}</Provider> : null
      }
    </ReactReduxContext.Consumer>,
    portalRef.current
  )
}

interface ModalContentProps {
  modal: ModalState
}

export const ModalContent = (props: ModalContentProps) => {
  const {
    ChildComponent,
    handleProceed,
    handleClose,
    hideCancel,
    message,
    wrapper,
    title
  } = props.modal

  const defaultHandleClose = () => {
    // Add default close handler if none provided
  }

  if (!ChildComponent) {
    return null
  }

  return (
    <>
      {wrapper ? (
        <ModalWrapper
          title={title}
          handleClose={handleClose || defaultHandleClose}
        >
          <ChildComponent
            message={message}
            hideCancel={hideCancel}
            onSubmit={handleProceed}
            handleClose={handleClose || defaultHandleClose}
            handleProceed={handleProceed}
            {...props.modal}
          />
        </ModalWrapper>
      ) : (
        <ChildComponent
          message={message}
          hideCancel={hideCancel}
          onSubmit={handleProceed}
          handleClose={handleClose || defaultHandleClose}
          handleProceed={handleProceed}
          {...props.modal}
        />
      )}
    </>
  )
}

interface ModalWrapperProps {
  title?: string
  handleClose?: () => void
  children: React.ReactNode
}

export const ModalWrapper = (props: ModalWrapperProps) => {
  const dispatch = useAppDispatch()

  const handleCloseClick = () => {
    if (props.handleClose) {
      dispatch(toggleModal({ active: false }))
      props.handleClose()
    }
  }

  return (
    <>
      <div className={styles.wrapperHeader}>
        <h3>{props.title}</h3>
        <Close onClick={handleCloseClick} size='medium' />
      </div>
      <div className={styles.wrapperContent}>{props.children}</div>
    </>
  )
}

type HandleClick = (event: React.MouseEvent<HTMLDivElement>) => void

interface CloseProps {
  color?: string
  size?: string
  onClick: HandleClick
}

export const Close = (props: CloseProps) => {
  let color = '#3E3E3E'
  if (props.color) color = props.color
  return (
    <div onClick={props.onClick} className={`${styles.close}`}>
      <svg
        className={props.size ? styles[props.size] : undefined}
        viewBox='0 0 12 12'
        version='1.1'
        xmlns='http://www.w3.org/2000/svg'
        xmlnsXlink='http://www.w3.org/1999/xlink'
      >
        <desc>Created with Sketch.</desc>
        <defs></defs>
        <g
          id='👨🏻‍💼-Activity-list'
          stroke='none'
          strokeWidth='1'
          fill='none'
          fillRule='evenodd'
        >
          <g
            id='View-events-Coach-Tablet'
            transform='translate(-991.000000, -56.000000)'
            stroke={color}
            strokeWidth='2'
          >
            <g id='Group' transform='translate(690.000000, 0.000000)'>
              <g id='icon/close' transform='translate(301.000000, 56.000000)'>
                <path d='M1,11 L11,1' id='icon/back'></path>
                <path d='M11,11 L1,1' id='Path-7'></path>
              </g>
            </g>
          </g>
        </g>
      </svg>
    </div>
  )
}

interface OverlayProps {
  active: boolean
  toggleModal: (modal: Partial<ModalState>) => void
}

export const ModalOverlay = ({ active, toggleModal }: OverlayProps) => {
  const [overlay, setOverlay] = useState(false)
  const [visible, setVisible] = useState(false)
  const [modalCount, setModalCount] = useState(0)

  // Track previous active state to detect changes
  const prevActiveRef = useRef<boolean>(undefined)
  // Pending reveal frame, cancelled on close/unmount.
  const revealFrameRef = useRef<number>(undefined)

  // Decrement the stacked-modal count once the close fade completes; unmount
  // the overlay when the last modal closes.
  const teardown = () => {
    setModalCount((prevCount) => {
      if (prevCount > 1) {
        return prevCount - 1
      }
      setOverlay(false)
      setVisible(false)
      return 0
    })
  }

  useEffect(() => {
    const prevActive = prevActiveRef.current

    if (!prevActive && active) {
      // Mount the overlay, then reveal on the next frame so the opacity
      // transition runs from 0.
      setOverlay(true)
      setModalCount((prev) => prev + 1)
      revealFrameRef.current = requestAnimationFrame(() => setVisible(true))
    } else if (prevActive && !active) {
      // Cancel a reveal that has not fired yet, else an open-then-close within
      // one frame would re-show the overlay after the close.
      if (revealFrameRef.current !== undefined) {
        cancelAnimationFrame(revealFrameRef.current)
        revealFrameRef.current = undefined
      }
      // Fade out, then tear down on transition end. Tear down immediately when
      // no opacity transition will run (reduced motion, or the overlay was
      // never revealed) so it can never stay mounted and block clicks.
      if (prefersReducedMotion() || !visible) {
        teardown()
      } else {
        setVisible(false)
      }
    }

    // Update the previous active state
    prevActiveRef.current = active
  }, [active])

  useEffect(() => {
    return () => {
      if (revealFrameRef.current !== undefined) {
        cancelAnimationFrame(revealFrameRef.current)
      }
    }
  }, [])

  const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (
      e.target === e.currentTarget &&
      e.propertyName === 'opacity' &&
      !visible
    ) {
      teardown()
    }
  }

  const handleOverlayClick = () => {
    toggleModal({})
  }

  return (
    <div>
      {overlay && (
        <div
          onClick={handleOverlayClick}
          onTransitionEnd={handleTransitionEnd}
          className={cn(styles.overlay, visible && styles.overlayVisible)}
        />
      )}
    </div>
  )
}
